// Page de gestion des avis clients - Boutique Pro
import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Star, MessageSquare, ThumbsUp, Flag, Reply, Clock,
  TrendingUp, Users, Filter, Check, X
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

// Composant pour afficher les étoiles
function StarRating({ rating, size = 16 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={size}
          className={star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}
        />
      ))}
    </div>
  );
}

// Avis de démonstration
const DEMO_REVIEWS = [
  {
    id: '1',
    author: 'Jean-Pierre M.',
    author_photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100',
    rating: 5,
    title: 'Excellent travail !',
    content: 'Intervention rapide pour une fuite dans la salle de bain. Travail propre et soigné. Je recommande vivement !',
    date: '2026-03-10',
    service: 'Réparation fuite',
    verified: true,
    helpful_count: 12,
    response: null
  },
  {
    id: '2',
    author: 'Marie L.',
    author_photo: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100',
    rating: 5,
    title: 'Rénovation salle de bain parfaite',
    content: 'Nous avons fait appel à eux pour refaire entièrement notre salle de bain. Le résultat est magnifique, dans les délais et le budget. Équipe très professionnelle.',
    date: '2026-03-05',
    service: 'Rénovation complète',
    verified: true,
    helpful_count: 8,
    response: 'Merci beaucoup Marie ! Ce fut un plaisir de travailler sur ce beau projet. N\'hésitez pas à nous recontacter !'
  },
  {
    id: '3',
    author: 'Thomas D.',
    author_photo: null,
    rating: 4,
    title: 'Bon service',
    content: 'Installation d\'un chauffe-eau. Bon travail dans l\'ensemble, juste un petit retard le jour de l\'intervention.',
    date: '2026-02-28',
    service: 'Installation chauffe-eau',
    verified: true,
    helpful_count: 3,
    response: null
  },
  {
    id: '4',
    author: 'Sophie B.',
    author_photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100',
    rating: 5,
    title: 'Dépannage urgent impeccable',
    content: 'Panne de chauffage un dimanche soir en plein hiver. Ils sont intervenus en urgence et ont tout réparé. Merci infiniment !',
    date: '2026-02-20',
    service: 'Dépannage chauffage',
    verified: true,
    helpful_count: 15,
    response: 'Merci Sophie ! La satisfaction de nos clients est notre priorité, même le dimanche 😊'
  }
];

export default function BoutiqueAvisPage() {
  const { user } = useOutletContext() || {};
  const [reviews, setReviews] = useState(DEMO_REVIEWS);
  const [filter, setFilter] = useState('all');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  const token = localStorage.getItem('clientToken');

  // Calculer les stats
  const stats = {
    total: reviews.length,
    average: (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1),
    distribution: {
      5: reviews.filter(r => r.rating === 5).length,
      4: reviews.filter(r => r.rating === 4).length,
      3: reviews.filter(r => r.rating === 3).length,
      2: reviews.filter(r => r.rating === 2).length,
      1: reviews.filter(r => r.rating === 1).length,
    }
  };

  const filteredReviews = filter === 'all' 
    ? reviews 
    : filter === 'pending' 
      ? reviews.filter(r => !r.response)
      : reviews.filter(r => r.rating === parseInt(filter));

  const handleReply = (reviewId) => {
    if (!replyText.trim()) {
      toast.error('Veuillez écrire une réponse');
      return;
    }

    setReviews(reviews.map(r => 
      r.id === reviewId ? { ...r, response: replyText } : r
    ));
    setReplyingTo(null);
    setReplyText('');
    toast.success('Réponse publiée !');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Avis Clients</h1>
        <p className="text-gray-500 mt-1">
          Gère les avis et témoignages de tes clients
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Star size={20} className="text-yellow-600" />
            </div>
            <span className="text-gray-500">Note moyenne</span>
          </div>
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-bold text-gray-900">{stats.average}</p>
            <span className="text-gray-500">/5</span>
          </div>
          <StarRating rating={Math.round(stats.average)} size={14} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <MessageSquare size={20} className="text-blue-600" />
            </div>
            <span className="text-gray-500">Total avis</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-100 rounded-lg">
              <ThumbsUp size={20} className="text-green-600" />
            </div>
            <span className="text-gray-500">Satisfaction</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {Math.round(((stats.distribution[5] + stats.distribution[4]) / stats.total) * 100)}%
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Reply size={20} className="text-orange-600" />
            </div>
            <span className="text-gray-500">À répondre</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {reviews.filter(r => !r.response).length}
          </p>
        </motion.div>
      </div>

      {/* Rating Distribution */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
      >
        <h2 className="font-semibold text-gray-900 mb-4">Répartition des notes</h2>
        <div className="space-y-2">
          {[5, 4, 3, 2, 1].map((rating) => (
            <div key={rating} className="flex items-center gap-3">
              <div className="flex items-center gap-1 w-16">
                <span className="text-sm font-medium">{rating}</span>
                <Star size={14} className="text-yellow-500 fill-yellow-500" />
              </div>
              <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-yellow-500 rounded-full"
                  style={{ width: `${(stats.distribution[rating] / stats.total) * 100}%` }}
                />
              </div>
              <span className="text-sm text-gray-500 w-8">{stats.distribution[rating]}</span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Filter */}
      <div className="flex items-center gap-3 overflow-x-auto pb-2">
        <span className="text-sm text-gray-500">Filtrer :</span>
        {[
          { value: 'all', label: 'Tous' },
          { value: 'pending', label: 'À répondre' },
          { value: '5', label: '5 étoiles' },
          { value: '4', label: '4 étoiles' },
          { value: '3', label: '3 étoiles et moins' },
        ].map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              filter === f.value
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {filteredReviews.map((review, index) => (
          <motion.div
            key={review.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {review.author_photo ? (
                  <img
                    src={review.author_photo}
                    alt={review.author}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-lg font-medium text-gray-500">
                      {review.author.charAt(0)}
                    </span>
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{review.author}</span>
                    {review.verified && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Check size={12} />
                        Vérifié
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <StarRating rating={review.rating} size={14} />
                    <span>•</span>
                    <span>{new Date(review.date).toLocaleDateString('fr-FR')}</span>
                  </div>
                </div>
              </div>
              
              {review.service && (
                <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                  {review.service}
                </span>
              )}
            </div>

            {/* Content */}
            <div className="mb-4">
              {review.title && (
                <h3 className="font-semibold text-gray-900 mb-1">{review.title}</h3>
              )}
              <p className="text-gray-600">{review.content}</p>
            </div>

            {/* Response */}
            {review.response && (
              <div className="bg-orange-50 rounded-lg p-4 mb-4 border-l-4 border-orange-500">
                <p className="text-sm font-medium text-orange-800 mb-1">Votre réponse :</p>
                <p className="text-sm text-orange-700">{review.response}</p>
              </div>
            )}

            {/* Reply Form */}
            {replyingTo === review.id && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Écris ta réponse..."
                  rows={3}
                  className="mb-3"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleReply(review.id)}
                    className="bg-orange-500 hover:bg-orange-600"
                    size="sm"
                  >
                    Publier la réponse
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setReplyingTo(null);
                      setReplyText('');
                    }}
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <ThumbsUp size={14} />
                  {review.helpful_count} utile
                </span>
              </div>
              
              <div className="flex gap-2">
                {!review.response && replyingTo !== review.id && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setReplyingTo(review.id)}
                  >
                    <Reply size={14} className="mr-1" />
                    Répondre
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="text-gray-500">
                  <Flag size={14} />
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredReviews.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
          <MessageSquare size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">Aucun avis dans cette catégorie</p>
        </div>
      )}
    </div>
  );
}
